import {
  QUALIFIED_ADD_TO_CART_GOAL_ID,
  QUALIFIED_CART_ADD_CONFIRMED_EVENT,
  chooseQualifiedAddToCartSession,
  createQualifiedAddToCartState,
  dispatchQualifiedAddToCartGoal,
  markQualifiedAddToCartConfirmed,
  parseQualifiedAddToCartState,
  updateQualifiedAddToCartVisibleActivity,
  type QualifiedAddToCartSessionCandidate,
  type QualifiedAddToCartState,
} from '~/utils/qualifiedAddToCart';
import { isRuntimeFlagEnabled } from '~/utils/runtimeFlag';

const SESSION_STORAGE_KEY = 'wtfish:qualified-add-to-cart:session-id';
const STATE_STORAGE_KEY_PREFIX = 'wtfish:qualified-add-to-cart:state:';
const LOCK_STORAGE_KEY_PREFIX = 'wtfish:qualified-add-to-cart:lock:';
const CHANNEL_NAME = 'wtfish:qualified-add-to-cart';
const SESSION_DISCOVERY_MS = 100;
const TICK_INTERVAL_MS = 1_000;
const FALLBACK_LOCK_DURATION_MS = 1_000;
const FALLBACK_LOCK_ATTEMPTS = 8;

type SessionMessage =
  | {
      candidate: QualifiedAddToCartSessionCandidate;
      sourceTabId: string;
      type: 'session-available' | 'session-discover';
    }
  | {
      sessionId: string;
      sourceTabId: string;
      type: 'state-updated';
    };

interface StoredLock {
  expiresAt: number;
  owner: string;
}

function delay(timeout: number) {
  return new Promise((resolve) => window.setTimeout(resolve, timeout));
}

function createId(): string {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function isSessionCandidate(value: unknown): value is QualifiedAddToCartSessionCandidate {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as Partial<QualifiedAddToCartSessionCandidate>;

  return (
    typeof candidate.sessionId === 'string' &&
    Boolean(candidate.sessionId) &&
    typeof candidate.startedAt === 'number' &&
    Number.isFinite(candidate.startedAt) &&
    candidate.startedAt >= 0
  );
}

function parseSessionMessage(value: unknown): SessionMessage | null {
  if (!value || typeof value !== 'object') return null;

  const message = value as Partial<SessionMessage>;

  if (typeof message.sourceTabId !== 'string') return null;

  if (
    (message.type === 'session-available' || message.type === 'session-discover') &&
    isSessionCandidate(message.candidate)
  ) {
    return message as SessionMessage;
  }

  if (message.type === 'state-updated' && typeof message.sessionId === 'string') return message as SessionMessage;

  return null;
}

class QualifiedAddToCartBrowserTracker {
  private readonly channel = new BroadcastChannel(CHANNEL_NAME);
  private readonly counterId: string | number;
  private readonly profileStore = useProfileStore();
  private readonly tabId = createId();

  private currentSession: QualifiedAddToCartSessionCandidate | null = null;
  private pendingConfirmedAdd = false;
  private sessionId: string | null = null;
  private stopped = false;
  private tickInterval: number | undefined;

  constructor(counterId: string | number) {
    this.counterId = counterId;
    this.channel.addEventListener('message', this.onChannelMessage);
    window.addEventListener(QUALIFIED_CART_ADD_CONFIRMED_EVENT, this.onConfirmedAdd);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('pagehide', this.onPageHide);
  }

  async start() {
    try {
      this.currentSession = await this.resolveSession();
      this.sessionId = this.currentSession.sessionId;

      await this.updateState((state) =>
        updateQualifiedAddToCartVisibleActivity(state, {
          isVisible: document.visibilityState === 'visible',
          now: Date.now(),
          tabId: this.tabId,
        }),
      );

      if (this.pendingConfirmedAdd) await this.persistConfirmedAdd();

      await this.tryDispatchGoal();

      if (!this.stopped) {
        this.tickInterval = window.setInterval(() => {
          void this.tick();
        }, TICK_INTERVAL_MS);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error(error);

      this.stop();
    }
  }

  stop() {
    if (this.stopped) return;

    void this.updateVisibility(false);
    this.stopped = true;

    if (this.tickInterval !== undefined) window.clearInterval(this.tickInterval);

    this.channel.removeEventListener('message', this.onChannelMessage);
    this.channel.close();
    window.removeEventListener(QUALIFIED_CART_ADD_CONFIRMED_EVENT, this.onConfirmedAdd);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('pagehide', this.onPageHide);
  }

  private readonly onChannelMessage = (event: MessageEvent<unknown>) => {
    const message = parseSessionMessage(event.data);

    if (!message || message.sourceTabId === this.tabId) return;

    if (message.type === 'session-discover' && this.currentSession) {
      this.postMessage({
        candidate: this.currentSession,
        sourceTabId: this.tabId,
        type: 'session-available',
      });

      return;
    }

    if (message.type === 'state-updated' && message.sessionId === this.sessionId) void this.tryDispatchGoal();
  };

  private readonly onConfirmedAdd = () => {
    this.pendingConfirmedAdd = true;
    void this.persistConfirmedAdd();
  };

  private readonly onVisibilityChange = () => {
    void this.updateVisibility(document.visibilityState === 'visible');
  };

  private readonly onPageHide = () => {
    void this.updateVisibility(false);
  };

  private postMessage(message: SessionMessage) {
    try {
      this.channel.postMessage(message);
    } catch {
      // Analytics coordination must never affect storefront behavior.
    }
  }

  private getStateStorageKey() {
    return `${STATE_STORAGE_KEY_PREFIX}${this.sessionId}`;
  }

  private readState(): QualifiedAddToCartState {
    const storedState = parseQualifiedAddToCartState(localStorage.getItem(this.getStateStorageKey()));

    if (storedState?.sessionId === this.sessionId) return storedState;

    return createQualifiedAddToCartState(this.currentSession!);
  }

  private writeState(state: QualifiedAddToCartState) {
    localStorage.setItem(this.getStateStorageKey(), JSON.stringify(state));
    this.postMessage({ sessionId: state.sessionId, sourceTabId: this.tabId, type: 'state-updated' });
  }

  private async resolveSession() {
    const storedSessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);

    if (storedSessionId) {
      const storedState = parseQualifiedAddToCartState(
        localStorage.getItem(`${STATE_STORAGE_KEY_PREFIX}${storedSessionId}`),
      );

      return {
        sessionId: storedSessionId,
        startedAt: storedState?.startedAt ?? Date.now(),
      };
    }

    const provisionalSession = { sessionId: createId(), startedAt: Date.now() };
    const candidates = new Map([[provisionalSession.sessionId, provisionalSession]]);
    const collectCandidate = (event: MessageEvent<unknown>) => {
      const message = parseSessionMessage(event.data);

      if (
        !message ||
        message.sourceTabId === this.tabId ||
        (message.type !== 'session-available' && message.type !== 'session-discover')
      ) {
        return;
      }

      candidates.set(message.candidate.sessionId, message.candidate);
    };

    this.currentSession = provisionalSession;
    this.channel.addEventListener('message', collectCandidate);
    this.postMessage({ candidate: provisionalSession, sourceTabId: this.tabId, type: 'session-discover' });
    await delay(SESSION_DISCOVERY_MS);
    this.channel.removeEventListener('message', collectCandidate);

    const selectedSession = chooseQualifiedAddToCartSession([...candidates.values()]) ?? provisionalSession;

    sessionStorage.setItem(SESSION_STORAGE_KEY, selectedSession.sessionId);

    return selectedSession;
  }

  private async tick() {
    if (this.stopped || document.visibilityState !== 'visible') return;

    await this.updateVisibility(true);
  }

  private async updateVisibility(isVisible: boolean) {
    if (!this.sessionId || this.stopped) return;

    const state = await this.updateState((currentState) =>
      updateQualifiedAddToCartVisibleActivity(currentState, {
        isVisible,
        now: Date.now(),
        tabId: this.tabId,
      }),
    );

    if (state) await this.tryDispatchGoal();
  }

  private async persistConfirmedAdd() {
    if (!this.sessionId || this.stopped || !this.pendingConfirmedAdd) return;

    const state = await this.updateState((currentState) => markQualifiedAddToCartConfirmed(currentState, Date.now()));

    if (!state) return;

    this.pendingConfirmedAdd = false;
    await this.tryDispatchGoal();
  }

  private canUseMetrika() {
    return (
      this.profileStore.userInfoLoadStatus === 'resolved' &&
      this.profileStore.userDetails?.analytics?.yandex_metrika_ecommerce_enabled !== false &&
      typeof window.ym === 'function'
    );
  }

  private async tryDispatchGoal() {
    if (!this.sessionId || this.stopped || !this.canUseMetrika()) return;

    await this.updateState((state) => {
      if (!this.canUseMetrika()) return state;

      return dispatchQualifiedAddToCartGoal(state, Date.now(), () => {
        window.ym!(this.counterId, 'reachGoal', QUALIFIED_ADD_TO_CART_GOAL_ID);
      });
    });
  }

  private async updateState(
    update: (state: QualifiedAddToCartState) => QualifiedAddToCartState,
  ): Promise<QualifiedAddToCartState | null> {
    if (!this.sessionId || !this.currentSession || this.stopped) return null;

    const runUpdate = () => {
      try {
        const currentState = this.readState();
        const nextState = update(currentState);

        if (nextState !== currentState) this.writeState(nextState);

        return nextState;
      } catch {
        return null;
      }
    };
    const lockName = `${CHANNEL_NAME}:${this.sessionId}`;

    if (navigator.locks) {
      try {
        return await navigator.locks.request(lockName, runUpdate);
      } catch {
        return null;
      }
    }

    return await this.updateStateWithFallbackLock(runUpdate);
  }

  private async updateStateWithFallbackLock(
    runUpdate: () => QualifiedAddToCartState | null,
  ): Promise<QualifiedAddToCartState | null> {
    const lockKey = `${LOCK_STORAGE_KEY_PREFIX}${this.sessionId}`;

    for (let attempt = 0; attempt < FALLBACK_LOCK_ATTEMPTS; attempt += 1) {
      const now = Date.now();
      let currentLock: StoredLock | null = null;

      try {
        currentLock = JSON.parse(localStorage.getItem(lockKey) ?? 'null') as StoredLock | null;
      } catch {
        currentLock = null;
      }

      if (!currentLock || currentLock.expiresAt <= now || currentLock.owner === this.tabId) {
        const ownLock = { expiresAt: now + FALLBACK_LOCK_DURATION_MS, owner: this.tabId };

        try {
          localStorage.setItem(lockKey, JSON.stringify(ownLock));
          await delay(10 + Math.floor(Math.random() * 10));

          const verifiedLock = JSON.parse(localStorage.getItem(lockKey) ?? 'null') as StoredLock | null;

          if (verifiedLock?.owner === this.tabId) {
            try {
              return runUpdate();
            } finally {
              const lockAfterUpdate = JSON.parse(localStorage.getItem(lockKey) ?? 'null') as StoredLock | null;

              if (lockAfterUpdate?.owner === this.tabId) localStorage.removeItem(lockKey);
            }
          }
        } catch {
          return null;
        }
      }

      await delay(15 + attempt * 5);
    }

    return null;
  }
}

let activeTracker: QualifiedAddToCartBrowserTracker | undefined;

export default defineNuxtPlugin(() => {
  const { isYandexMetrikaEnabled, yandexMetrikaId } = useRuntimeConfig().public;

  if (!isRuntimeFlagEnabled(isYandexMetrikaEnabled) || !yandexMetrikaId) return;

  try {
    activeTracker?.stop();
    activeTracker = new QualifiedAddToCartBrowserTracker(yandexMetrikaId);
    void activeTracker.start();
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.error(error);
  }
});

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    activeTracker?.stop();
    activeTracker = undefined;
  });
}
